import {
    getMediaTrack,
    getAudioTrack,
    getEnabledSegments,
    computeTimelineDurationMs
} from '../timelineModel.js'
import { timeStretchAudioBuffer } from './timeStretch.js'

const arrayBufferCache = new Map()

const fetchArrayBuffer = async (url) => {
    if (arrayBufferCache.has(url)) return arrayBufferCache.get(url)
    const promise = fetch(url).then(r => r.arrayBuffer())
    arrayBufferCache.set(url, promise)
    return promise
}

const decodeAudio = async (ctx, url) => {
    const arrayBuffer = await fetchArrayBuffer(url)
    // decodeAudioData detaches the buffer it's given - always hand it a copy.
    return ctx.decodeAudioData(arrayBuffer.slice(0))
}

// Extracts [offsetSec, offsetSec + durationSec) from a decoded buffer into its own standalone
// AudioBuffer - needed before time-stretching a segment, since timeStretchAudioBuffer operates on
// (and reshapes the length of) the whole buffer it's given, not just a windowed region of it.
const sliceAudioBuffer = (ctx, buffer, offsetSec, durationSec) => {
    const sampleRate = buffer.sampleRate
    const startSample = Math.max(0, Math.floor(offsetSec * sampleRate))
    const length = Math.max(1, Math.floor(durationSec * sampleRate))
    const endSample = Math.min(buffer.length, startSample + length)
    const actualLength = Math.max(1, endSample - startSample)

    const sliced = ctx.createBuffer(buffer.numberOfChannels, actualLength, sampleRate)
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
        sliced.copyToChannel(buffer.getChannelData(ch).subarray(startSample, startSample + actualLength), ch)
    }
    return sliced
}

// Offline-renders the timeline's full audio mix (each video clip's original audio, respecting
// trims/enabled segments and concatenation order, plus the background music track with basic
// fade in/out) into a single stereo AudioBuffer, ready to feed to mediabunny's AudioBufferSource.
// This is NOT real-time - it can run faster or slower than 1x, which is the whole point of doing
// this offline rather than trying to capture live preview audio.
export const renderMixedAudioBuffer = async (timeline, resolveSource, { sampleRate = 44100 } = {}) => {
    const durationSec = computeTimelineDurationMs(timeline) / 1000
    if (durationSec <= 0) return null

    const offlineCtx = new OfflineAudioContext(2, Math.max(1, Math.ceil(durationSec * sampleRate)), sampleRate)
    let hasAnySource = false

    const mediaTrack = getMediaTrack(timeline)
    for (const clip of (mediaTrack ? mediaTrack.clips : [])) {
        if (clip.sourceType !== 'video' || clip.muted) continue
        const source = resolveSource(clip.fileId)
        if (!source) continue

        let audioBuffer
        try {
            audioBuffer = await decodeAudio(offlineCtx, source.url)
        } catch (e) {
            continue // no decodable audio track on this clip (e.g. silent video) - skip it
        }

        const segments = getEnabledSegments(clip)
        const segmentsToUse = segments.length ? segments : [{ startMs: 0, endMs: clip.trimEndMs - clip.trimStartMs }]
        const rate = clip.playbackRate || 1
        // Matches ClipToolPanel's "conserver la hauteur" toggle for the "Vitesse" tool - the live
        // preview gets this for free from the <video> element's own automatic pitch correction
        // (see TimelinePlayer.js), which a raw AudioBufferSourceNode.playbackRate has no equivalent
        // for; time-stretching the decoded segment first is what closes that gap for export.
        const preservePitch = clip.preservePitch !== false

        // cursorMs walks forward in *timeline* time (matching clip.timelineStartMs, which
        // recomputeTimelineLayout already derives from the speed-adjusted effective duration - see
        // getEffectiveClipDuration) - so each step must advance by the segment's effective
        // (rate-divided) length, not its raw source length, or every clip after a sped-up one
        // drifts out of sync with its own video by exactly the amount that segment was shortened.
        let cursorMs = clip.timelineStartMs
        for (const seg of segmentsToUse) {
            const segDurationMs = seg.endMs - seg.startMs
            if (segDurationMs <= 0) continue
            const effectiveDurationMs = segDurationMs / rate

            const srcOffsetSec = (clip.trimStartMs + seg.startMs) / 1000
            const srcDurationSec = segDurationMs / 1000
            if (srcOffsetSec >= audioBuffer.duration) { cursorMs += effectiveDurationMs; continue }
            const clampedDurationSec = Math.min(srcDurationSec, audioBuffer.duration - srcOffsetSec)

            const gainNode = offlineCtx.createGain()
            gainNode.gain.value = clip.volume !== undefined ? clip.volume : 1

            const bufferSource = offlineCtx.createBufferSource()
            if (rate !== 1 && preservePitch) {
                const slice = sliceAudioBuffer(offlineCtx, audioBuffer, srcOffsetSec, clampedDurationSec)
                bufferSource.buffer = timeStretchAudioBuffer(offlineCtx, slice, rate)
                bufferSource.connect(gainNode).connect(offlineCtx.destination)
                bufferSource.start(cursorMs / 1000)
            } else {
                bufferSource.buffer = audioBuffer
                // Without this, a clip playing back at 2x visually still had its original audio
                // play at normal speed underneath - the "video sped up, sound didn't" desync.
                bufferSource.playbackRate.value = rate
                bufferSource.connect(gainNode).connect(offlineCtx.destination)
                bufferSource.start(cursorMs / 1000, srcOffsetSec, clampedDurationSec)
            }
            hasAnySource = true
            cursorMs += effectiveDurationMs
        }
    }

    // Several background music tracks can be laid out on the audio track (sequentially or
    // overlapping) - each is scheduled independently, exactly like the live preview mixes them.
    const audioTrack = getAudioTrack(timeline)
    for (const musicClip of (audioTrack ? audioTrack.clips : [])) {
        if (musicClip.muted) continue
        const source = resolveSource(musicClip.fileId)
        if (!source) continue

        try {
            const audioBuffer = await decodeAudio(offlineCtx, source.url)
            const bufferSource = offlineCtx.createBufferSource()
            bufferSource.buffer = audioBuffer
            const gainNode = offlineCtx.createGain()
            bufferSource.connect(gainNode).connect(offlineCtx.destination)

            const startSec = musicClip.timelineStartMs / 1000
            const endSec = musicClip.timelineEndMs / 1000
            const volume = musicClip.volume !== undefined ? musicClip.volume : 1
            const fadeInSec = (musicClip.fadeInMs || 0) / 1000
            const fadeOutSec = (musicClip.fadeOutMs || 0) / 1000

            gainNode.gain.setValueAtTime(0, startSec)
            gainNode.gain.linearRampToValueAtTime(volume, startSec + fadeInSec)
            gainNode.gain.setValueAtTime(volume, Math.max(startSec + fadeInSec, endSec - fadeOutSec))
            gainNode.gain.linearRampToValueAtTime(0, endSec)

            const srcDurationSec = (musicClip.trimEndMs - musicClip.trimStartMs) / 1000
            bufferSource.start(startSec, musicClip.trimStartMs / 1000, srcDurationSec)
            hasAnySource = true
        } catch (e) {
            // this background track isn't decodable - skip it, the rest of the mix still renders
        }
    }

    if (!hasAnySource) return null
    return offlineCtx.startRendering()
}
